import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaHome,
  FaUserCircle,
  FaCog,
  FaSignOutAlt,
  FaPhoneAlt,
  FaUserPlus,
  FaCalendarCheck,
  FaEnvelope,
} from "react-icons/fa";
import { HiChevronDown, HiChevronUp, HiOutlineMenuAlt2 } from "react-icons/hi";
import { LuSearch } from "react-icons/lu";
import { MdOutlineNotificationsNone } from "react-icons/md";
import { useAuth } from "../../../utils/AuthContext";
import { sidebarSections } from "../../../utils/menuData";
import SyncStatusIndicator from "../../../components/Pathology/SyncStatusIndicator";
import { getPendingCount } from "../../../services/pathologyOutbox";
import "react-toastify/dist/ReactToastify.css";

// Custom hook for handling outside clicks on dropdowns
const useOutsideClickHandler = (initialState = false, triggerRef = null) => {
  const [isOpen, setIsOpen] = useState(initialState);
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      const isOutsideDropdown =
        ref.current && !ref.current.contains(event.target);
      const isTriggerClick =
        triggerRef?.current && triggerRef.current.contains(event.target);

      if (isOpen && isOutsideDropdown && !isTriggerClick) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, triggerRef]);

  return { ref, isOpen, setIsOpen };
};

const TopHeader = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const { user, logout } = useAuth();
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [notifications, setNotifications] = useState([]);
  const [logoutModal, setLogoutModal] = useState({ open: false, pending: 0 });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const isAdminOrSuperAdmin =
    user?.role === "admin" || user?.role === "superadmin";

  // নোটিফিকেশনকে দুটি আলাদা গ্রুপে ভাগ করা হয়েছে
  const callbackNotifications = notifications.filter(
    (n) => n.type === "NEW_CALLBACK_REQUEST"
  );
  const otherNotifications = notifications.filter(
    (n) => n.type !== "NEW_CALLBACK_REQUEST"
  );

  // ফিল্টার করা, অপঠিত নোটিফিকেশনের উপর ভিত্তি করে কাউন্ট করা হয়েছে
  const callbackNotificationsCount = callbackNotifications.filter(
    (n) => !n.readStatus
  ).length;
  const otherNotificationsCount = otherNotifications.filter(
    (n) => !n.readStatus
  ).length;

  const navigate = useNavigate();
  const API_KEY =
    "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

  // Refs for the buttons
  const dropdownButtonRef = useRef(null);
  const notifButtonRef = useRef(null);
  const callButtonRef = useRef(null);

  // Using the custom hook for both dropdowns
  const {
    ref: dropdownRef,
    isOpen: dropdownOpen,
    setIsOpen: setDropdownOpen,
  } = useOutsideClickHandler(false, dropdownButtonRef);

  const {
    ref: notifdropdownRef,
    isOpen: notifDropdownOpen,
    setIsOpen: setNotifDropdownOpen,
  } = useOutsideClickHandler(false, notifButtonRef);

  const {
    ref: callDropdownRef,
    isOpen: callDropdownOpen,
    setIsOpen: setCallDropdownOpen,
  } = useOutsideClickHandler(false, callButtonRef);

  // Update isMobile on window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch notifications from backend API on mount (এটি সব নোটিফিকেশন নিয়ে আসবে)
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await fetch(
          "https://api.muktihospital.com/api/notifications",
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "x-api-key": API_KEY,
            },
            credentials: "include",
          }
        );
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        const data = await res.json();
        setNotifications(data.notifications || []);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    fetchNotifications();
  }, [API_KEY]);

  // Handle search input changes
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.length > 1) {
      const isAdminOrSuperAdmin =
        user?.role === "admin" || user?.role === "superadmin";
      const results = [];

      sidebarSections.forEach((section) => {
        section.menus.forEach((menu) => {
          if (
            (menu.isAdmin === true || menu.isSuperAdmin === true) &&
            !isAdminOrSuperAdmin
          )
            return;

          if (
            menu.name.toLowerCase().includes(query.toLowerCase()) &&
            !menu.isSubmenu &&
            menu.link !== "#" &&
            menu.link !== ""
          ) {
            results.push({
              name: menu.name,
              link: menu.link,
              section: section.title,
            });
          }

          if (menu.submenus) {
            menu.submenus.forEach((submenu) => {
              if (
                (submenu.isAdmin === true || submenu.isSuperAdmin === true) &&
                !isAdminOrSuperAdmin
              )
                return;

              if (submenu.name.toLowerCase().includes(query.toLowerCase())) {
                results.push({
                  name: submenu.name,
                  link: submenu.link,
                  section: section.title,
                  parent: menu.name,
                });
              }
            });
          }
        });
      });

      setSearchResults(results);
      setSearchDropdownOpen(true);
    } else {
      setSearchResults([]);
      setSearchDropdownOpen(false);
    }
  };

  // Handle search result click
  const handleSearchResultClick = (link) => {
    setSearchQuery("");
    setSearchResults([]);
    setSearchDropdownOpen(false);
    navigate(link);
  };

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".search-container")) {
        setSearchDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Function to mark a notification as read
  const handleMarkRead = async (notificationId) => {
    try {
      const res = await fetch(
        `https://api.muktihospital.com/api/notifications/mark-read/${notificationId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": API_KEY,
          },
        }
      );
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      setNotifications((prevNotifications) =>
        prevNotifications.map((notif) =>
          notif.id === notificationId ? { ...notif, readStatus: true } : notif
        )
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // Handle BELL icon click: toggle dropdown and mark OTHER notifications as read
  const handleNotificationIconClick = () => {
    setNotifDropdownOpen((prev) => !prev);
    handleMarkAllOtherRead();
  };

  // Handle CALL icon click: toggle dropdown and mark CALLBACK notifications as read
  const handleCallIconClick = () => {
    setCallDropdownOpen((prev) => !prev);
    handleMarkAllCallbackRead();
  };

  // Function to mark all unread OTHER notifications as read
  const handleMarkAllOtherRead = async () => {
    const unreadNotifications = otherNotifications.filter((n) => !n.readStatus);
    if (unreadNotifications.length === 0) return;
    try {
      await Promise.all(
        unreadNotifications.map((notif) =>
          fetch(
            `https://api.muktihospital.com/api/notifications/mark-read/${notif.id}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                "x-api-key": API_KEY,
              },
            }
          )
        )
      );
      // Update the main notifications state
      setNotifications((prevNotifications) =>
        prevNotifications.map((notif) =>
          notif.type !== "NEW_CALLBACK_REQUEST"
            ? { ...notif, readStatus: true }
            : notif
        )
      );
    } catch (error) {
      console.error("Error marking other notifications as read:", error);
    }
  };

  // Function to mark all unread CALLBACK notifications as read
  const handleMarkAllCallbackRead = async () => {
    const unreadNotifications = callbackNotifications.filter(
      (n) => !n.readStatus
    );
    if (unreadNotifications.length === 0) return;
    try {
      await Promise.all(
        unreadNotifications.map((notif) =>
          fetch(
            `https://api.muktihospital.com/api/notifications/mark-read/${notif.id}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                "x-api-key": API_KEY,
              },
            }
          )
        )
      );
      // Update the main notifications state
      setNotifications((prevNotifications) =>
        prevNotifications.map((notif) =>
          notif.type === "NEW_CALLBACK_REQUEST"
            ? { ...notif, readStatus: true }
            : notif
        )
      );
    } catch (error) {
      console.error("Error marking callback notifications as read:", error);
    }
  };


  // Perform the actual logout
  const doLogout = () => {
    try {
      logout();
      window.location.href = "https://muktihospital.com/logout";
    } catch (error) {
      console.error("Logout failed:", error);
      alert("Failed to log out. Please try again.");
    }
  };

  // Handle logout — warn first if there are un-synced offline bills.
  const handleLogout = async () => {
    let pending = 0;
    try {
      pending = await getPendingCount();
    } catch (e) {
      pending = 0;
    }
    if (pending > 0) {
      setLogoutModal({ open: true, pending });
      return;
    }
    doLogout();
  };

  const renderNotificationIcon = (type) => {
    const iconClass = "mr-3 shrink-0";
    const iconSize = 16;

    switch (type) {
      case "NEW_CALLBACK_REQUEST":
        return (
          <FaPhoneAlt
            className={`${iconClass} text-blue-500`}
            size={iconSize}
          />
        );
      case "APPOINTMENT_CONFIRMED":
      case "APPOINTMENT_APPROVED":
        return (
          <FaCalendarCheck
            className={`${iconClass} text-green-500`}
            size={iconSize}
          />
        );
      case "APPOINTMENT_CANCELLED":
        return (
          <FaCalendarCheck
            className={`${iconClass} text-red-500`}
            size={iconSize}
          />
        );
      case "NEW_USER_REGISTERED":
        return (
          <FaUserPlus
            className={`${iconClass} text-indigo-500`}
            size={iconSize}
          />
        );
      case "NEW_CONTACT_MESSAGE":
        return (
          <FaEnvelope
            className={`${iconClass} text-orange-500`}
            size={iconSize}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-white text-gray-800 px-5 flex justify-between items-center shadow shadow-[#6886b10e] relative h-[73px]">
      <div className="flex items-center gap-3">
        {isMobile && (
          <button
            className="text-black transition-all"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          >
            <HiOutlineMenuAlt2 size={24} />
          </button>
        )}
        <div className="relative w-72 md:flex hidden search-container">
          <input
            type="text"
            placeholder="Search menus..."
            className="w-60 px-3 py-[10px] pl-9 rounded-md ring-0 focus:ring-1 focus:ring-M-primary-color outline-none duration-300 font-inter text-[14px] bg-[#f0f3f8]"
            value={searchQuery}
            onChange={handleSearchChange}
          />
          <LuSearch
            size={18}
            className="absolute left-3 top-[12px] text-[#9ba6b7]"
          />
          {searchDropdownOpen && searchResults.length > 0 && (
            <div className="absolute top-12 left-0 w-full bg-white border rounded-md shadow-md z-50 max-h-64 overflow-y-auto">
              <ul>
                {searchResults.map((result, index) => (
                  <li
                    key={index}
                    className="p-3 text-sm cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSearchResultClick(result.link)}
                  >
                    <span className="font-medium">{result.name}</span>
                    <div className="text-xs text-gray-500">
                      {result.parent
                        ? `${result.section} > ${result.parent}`
                        : result.section}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {searchDropdownOpen &&
            searchResults.length === 0 &&
            searchQuery.length > 1 && (
              <div className="absolute top-12 left-0 w-full bg-white border rounded-md shadow-md z-50 p-3">
                <p className="text-sm text-gray-500">No results found</p>
              </div>
            )}
        </div>
      </div>
      <div className="flex items-center gap-3 lg:gap-6">
        <Link to="/" className="cursor-pointer">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 32 24"
          >
            {/* SVG content here */}
          </svg>
        </Link>
        <SyncStatusIndicator apiKey={API_KEY} />
        {isAdminOrSuperAdmin && (
          <>
          <div className="relative">
          <button
            ref={callButtonRef}
            onClick={handleCallIconClick}
            className="relative text-[#6c757d] hover:text-M-primary-color top-1"
            title="Callback Requests"
          >
            <FaPhoneAlt size={18} />
            {callbackNotificationsCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
                {callbackNotificationsCount}
              </span>
            )}
          </button>
          {callDropdownOpen && (
            <div
              ref={callDropdownRef}
              className="absolute right-0 mt-2 w-80 bg-white border rounded shadow-md z-50"
            >
              <div className="p-3 border-b bg-gray-100">
                <h4 className="font-bold text-gray-700">Callback Requests</h4>
              </div>
              <ul className="max-h-64 overflow-auto">
                {callbackNotifications.length === 0 ? (
                  <li className="p-3 text-gray-500 text-sm">
                    No callback requests
                  </li>
                ) : (
                  callbackNotifications.map((notif) => (
                    <li
                      key={notif.id}
                      className="p-3 border-b text-sm cursor-pointer hover:bg-gray-50"
                      onClick={() => handleMarkRead(notif.id)}
                    >
                      <div className="flex items-center">
                        {renderNotificationIcon(notif.type)}
                        <span className="flex-1">{notif.message}</span>
                      </div>
                    </li>
                  ))
                )}
              </ul>
              <div className="p-3 text-right bg-gray-100">
                <Link
                  to="/callback-requests"
                  className="text-M-primary-color hover:underline text-sm"
                >
                  See all
                </Link>
              </div>
            </div>
          )}
        </div>
          </>
        )}
         {isAdminOrSuperAdmin && (
          <>
             <button
            ref={notifButtonRef}
            onClick={handleNotificationIconClick}
            className="relative text-[#6c757d] hover:text-M-primary-color top-1"
            title="Notifications"
          >
            <MdOutlineNotificationsNone size={22} />
            {otherNotificationsCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
                {otherNotificationsCount}
              </span>
            )}
          </button>
           <div className="relative">
          {notifDropdownOpen && (
            <div
              ref={notifdropdownRef}
              className="absolute right-0 mt-2 w-80 bg-white border rounded shadow-md z-50"
            >
              <div className="p-3 border-b bg-gray-100">
                <h4 className="font-bold text-gray-700">Notifications</h4>
              </div>
              <ul className="max-h-64 overflow-auto">
                {otherNotifications.length === 0 ? (
                  <li className="p-3 text-gray-500 text-sm">
                    No new notifications
                  </li>
                ) : (
                  otherNotifications.map((notif) => (
                    <li
                      key={notif.id}
                      className="p-3 border-b text-sm cursor-pointer hover:bg-gray-50"
                      onClick={() => handleMarkRead(notif.id)}
                    >
                      <div className="flex items-center">
                        {renderNotificationIcon(notif.type)}
                        <span className="flex-1">{notif.message}</span>
                      </div>
                    </li>
                  ))
                )}
              </ul>
              <div className="p-3 text-right bg-gray-100">
                <Link
                  to="/notifications"
                  className="text-M-primary-color hover:underline text-sm"
                >
                  See all
                </Link>
              </div>
            </div>
          )}
        </div>
          </>
         )}
        <Link
          to="https://muktihospital.com/"
          className="text-gray-600 hover:text-gray-800 hidden sm:block"
          title="Home"
        >
          <FaHome size={20} />
        </Link>

        <div className="relative h-[73px] flex bg-[#fafafd] border-x-[#eef2f7]">
          <button
            ref={dropdownButtonRef}
            className="flex items-center gap-3 transition-all font-inter px-3 py-0 border-x border-[#eef2f7] group"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            {user && user.profilePhoto ? (
                <img
                src={`https://api.muktihospital.com${user.profilePhoto}`}
                alt="User Profile"
                className="w-8 h-8 rounded-full object-cover"
                onError={(e) => {
                  if (e.target.dataset.fallback) return;
                  e.target.dataset.fallback = "1";
                  e.target.onerror = null;
                  e.target.src =
                    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32'%3E%3Crect width='32' height='32' rx='16' fill='%23cbd5e1'/%3E%3C/svg%3E";
                }}
              />
            ) : (
              <img
                src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32'%3E%3Crect width='32' height='32' rx='16' fill='%23cbd5e1'/%3E%3C/svg%3E"
                alt="Placeholder Profile"
                className="w-8 h-8 rounded-full"
              />
            )}
            <span className="text-sm text-left capitalize font-medium truncate w-16 group-hover:text-M-primary-color">
              {user ? user.name : "Loading..."}
            </span>
            {dropdownOpen ? (
              <HiChevronUp
                className="group-hover:text-M-primary-color"
                size={18}
              />
            ) : (
              <HiChevronDown
                className="group-hover:text-M-primary-color"
                size={18}
              />
            )}
          </button>

          {dropdownOpen && (
            <div
              ref={dropdownRef}
              className="absolute top-16 right-0 mt-2 w-full bg-white rounded-md border border-[#e7e9eb] shadow-md p-1 z-50"
            >
              <Link
                to="/profile"
                className="flex items-center gap-3 px-4 py-2 hover:bg-gray-200 transition rounded text-sm"
              >
                <FaUserCircle /> Profile
              </Link>
              <Link
                to="/profile"
                className="flex items-center gap-3 px-4 py-2 hover:bg-gray-200 transition rounded text-sm"
              >
                <FaCog /> Settings
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-2 w-full text-left text-red-400 hover:bg-red-100 hover:text-red-600 transition rounded text-sm"
              >
                <FaSignOutAlt /> Logout
              </button>
            </div>
          )}
        </div>
      </div>
      {logoutModal.open && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-2">
              Unsynced Bills Warning
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {logoutModal.pending} bill(s) created offline have not been
              synced to the server yet. Logging out now may cause these bills
              to be lost. Please make sure you are connected to the internet
              and let them sync first.
            </p>
            <p className="text-sm font-medium text-gray-700 mb-2">
              Do you still want to log out?
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setLogoutModal({ open: false, pending: 0 })}
                className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
              >
                Stay & Sync
              </button>
              <button
                onClick={() => {
                  setLogoutModal({ open: false, pending: 0 });
                  doLogout();
                }}
                className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
              >
                Logout Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TopHeader;         